import type { StoreApi } from "zustand";
import type { TChatStore } from "../chat-store.types";
import type {
  TConversation,
  TBlockUserPayload,
  TConversationDirection,
} from "./conversation.types";
import { conversationListInitialState } from "./conversation.initial-state";
import { USER_TAG } from "@/utils/constants";
import { arrayOfDistincObj } from "./utils/conversation.helpers";

type TSetState = StoreApi<TChatStore>["setState"];

export const createConversationListActions = (set: TSetState) => ({
  setConversationListLoading: (
    direction: TConversationDirection,
    chatType?: string,
  ) =>
    set((state) => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      return { [key]: { ...state[key], idle: false, loading: true, chatType } };
    }),

  setConversationListFulfilled: (
    direction: TConversationDirection,
    conversations: TConversation[],
    chatType?: string,
  ) =>
    set((state) => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      const slice = state[key];
      const source = slice.fetched
        ? arrayOfDistincObj(
            [...slice.source, ...conversations],
            "chatId",
          )
        : conversations;
      return {
        [key]: {
          ...conversationListInitialState,
          idle: false,
          fetched: true,
          source,
          chatType,
        },
      };
    }),

  setConversationListRejected: (
    direction: TConversationDirection,
    source: TConversation[],
  ) =>
    set(() => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      return {
        [key]: { ...conversationListInitialState, error: true, source },
      };
    }),

  setConversationListUpdated: (
    direction: TConversationDirection,
    source: TConversation[],
  ) =>
    set((state) => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      return { [key]: { ...state[key], source, updated: true } };
    }),

  resetConversationList: (direction: TConversationDirection) =>
    set((state) => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      return {
        [key]: conversationListInitialState,
        conversation: {
          ...state.conversation,
          status: {
            ...state.conversation.status,
            [direction]: "idle",
          },
          hasOlderChats: {
            ...state.conversation.hasOlderChats,
            [direction]: undefined,
          },
          empty: { ...state.conversation.empty, [direction]: true },
        },
      };
    }),

  updateUnreadMessage: (
    direction: TConversationDirection,
    payload: { chatId: string; unreadMessages: number },
  ) =>
    set((state) => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      return {
        [key]: {
          ...state[key],
          source: state[key].source.map((chat: TConversation) =>
            chat.chatId === payload?.chatId
              ? { ...chat, unreadMessages: payload.unreadMessages }
              : chat,
          ),
        },
      };
    }),

  blockUserConversationList: (
    direction: TConversationDirection,
    payload: TBlockUserPayload,
  ) =>
    set((state) => {
      const key = direction === "to" ? "conversationTo" : "conversationFrom";
      return {
        [key]: {
          ...state[key],
          source: state[key].source.map((chat: TConversation) =>
            chat.chatId === payload?.chatId
              ? {
                  ...chat,
                  userTag: payload.blocked ? USER_TAG.Blocked : USER_TAG.NaturalPerson,
                }
              : chat,
          ),
        },
      };
    }),
});
